import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatNumber } from '../services/utils';
import { fetchCurrencyRates } from '../services/ratesAPI';

const dkkBadge = (
  <span className="inline-flex items-center rounded-full bg-company-main/15 px-2.5 py-0.5 text-xs font-semibold text-company-main">DKK</span>
);

const CURRENCIES = [
  'DKK','EUR','USD','GBP','SEK','NOK','CHF','CAD','AUD','NZD',
  'JPY','CNY','HKD','SGD','PLN','CZK','HUF','RON','BGN','ISK',
  'TRY','BRL','MXN','ZAR','THB','MYR','PHP','INR','IDR','ILS','KRW',
  'AED','HRK','LTL','MAD','OMR','RUB','SCR'
];

function TotalsSection({ cost = 0, net = 0, vat = 0, fxFetchedAt, onCurrencyChange, onAdjustTotal, isReadOnly = false }) {
  const [displayCurrency, setDisplayCurrency] = useState('DKK');
  const [buyRate, setBuyRate] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [editingTotal, setEditingTotal] = useState(false);
  const [editValue, setEditValue] = useState('');
  const dropdownRef = useRef(null);

  const total = net + vat;
  const profit = net - cost;
  const profitPercent = net !== 0 ? (profit / Math.abs(net)) * 100 : 0;

  // Fetch buy rate when currency or the offer's rate snapshot changes.
  // Re-running on fxFetchedAt keeps the foreign-currency Total in sync after
  // "Pull New Exchange Rates" without forcing the user to re-pick the currency.
  // Buy rate is used for both directions of conversion so that round-tripping
  // (display foreign → edit → adjust DKK via OKORREKT → re-display foreign)
  // lands on the user's typed value.
  useEffect(() => {
    if (displayCurrency === 'DKK') {
      setBuyRate(1);
      return;
    }
    let cancelled = false;
    fetchCurrencyRates(displayCurrency)
      .then(({ buyRate: br }) => {
        if (!cancelled) setBuyRate(br);
      })
      .catch(err => { console.error('Failed to fetch rates:', err); if (!cancelled) setBuyRate(1); });
    return () => { cancelled = true; };
  }, [displayCurrency, fxFetchedAt]);

  // Notify parent when currency, total, or rate changes
  useEffect(() => {
    if (onCurrencyChange) {
      const converted = displayCurrency === 'DKK' ? total : (buyRate > 0 ? total / buyRate : 0);
      onCurrencyChange(displayCurrency, converted, buyRate);
    }
  }, [displayCurrency, total, buyRate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const convertedTotal = displayCurrency === 'DKK' ? total : (buyRate > 0 ? total / buyRate : 0);

  const handleTotalBlur = useCallback(() => {
    setEditingTotal(false);
    const normalized = String(editValue).replace(/\./g, '').replace(',', '.');
    const newAmount = parseFloat(normalized);
    if (!Number.isFinite(newAmount)) return;

    if (displayCurrency === 'DKK') {
      const diff = newAmount - total;
      if (Math.abs(diff) < 0.005) return;
      if (onAdjustTotal) onAdjustTotal(diff);
    } else {
      const diff = newAmount - convertedTotal;
      if (Math.abs(diff) < 0.005) return;
      const diffDKK = diff * buyRate;
      if (onAdjustTotal) onAdjustTotal(diffDKK);
    }
  }, [editValue, total, convertedTotal, buyRate, displayCurrency, onAdjustTotal]);

  const currencyBadge = (cur, bold) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${bold ? 'bg-company-main text-white' : 'bg-company-main/15 text-company-main'}`}>
      {cur}
    </span>
  );

  const currencyButton = isReadOnly ? currencyBadge(displayCurrency, true) : (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen(prev => !prev)}
        className="inline-flex items-center rounded-full bg-company-main px-2.5 py-0.5 text-xs font-semibold text-white cursor-pointer hover:brightness-95 transition"
      >
        {displayCurrency}
      </button>
      {dropdownOpen && (
        <div className="absolute z-50 mt-1 left-0 max-h-60 w-24 overflow-y-auto rounded-lg border border-black/10 bg-white shadow-xl">
          {CURRENCIES.map(cur => (
            <button
              key={cur}
              type="button"
              className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-company-toned transition ${cur === displayCurrency ? 'font-bold text-company-main bg-company-toned/60' : 'text-black/70'}`}
              onClick={() => { setDisplayCurrency(cur); setDropdownOpen(false); }}
            >
              {cur}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
  <div className="flex justify-end mb-8 font-sans">
    <div className="w-[400px] rounded-xl bg-company-toned p-5 shadow-lg">
      <table className="w-full text-sm text-black/70 border-collapse">
        <colgroup>
          <col className="w-[80px]" />
          <col className="w-[80px]" />
          <col className="w-[120px]" />
        </colgroup>
        <tbody>
          {/* Cost */}
          <tr>
            <td className="py-1.5 font-medium text-black/80">Cost:</td>
            <td className="py-1.5 text-left">{dkkBadge}</td>
            <td className="py-1.5 text-right font-semibold text-black/80">
              {formatNumber(cost, 2)}
            </td>
          </tr>

          {/* Net */}
          <tr>
            <td className="py-1.5 font-medium text-black/80">Net:</td>
            <td className="py-1.5">{dkkBadge}</td>
            <td className="py-1.5 text-right font-semibold text-black/80">
              {formatNumber(net, 2)}
            </td>
          </tr>

          {/* VAT */}
          <tr>
            <td className="py-1.5 font-medium text-black/80">VAT:</td>
            <td className="py-1.5">{dkkBadge}</td>
            <td className="py-1.5 text-right font-semibold text-black/80">
              {formatNumber(vat, 2)}
            </td>
          </tr>

        </tbody>
      </table>

      {/* Divider */}
      <div className="mt-2 border-t border-black/10 pt-2">
        <table className="w-full border-collapse">
          <colgroup>
            <col className="w-[80px]" />
            <col className="w-[80px]" />
            <col className="w-[120px]" />
        </colgroup>
          <tbody>
            {/* Total */}
            <tr className="text-lg font-semibold text-black">
              <td className="py-1.5">Total:</td>
              <td className="py-1.5">{currencyButton}</td>
              <td className="py-1.5 text-right">
                {editingTotal ? (
                  <input
                    type="text"
                    className="w-full rounded-md border border-company-main/40 bg-white px-2 py-0.5 text-right text-lg font-semibold text-black focus:border-company-main focus:outline-none focus:ring-2 focus:ring-company-main/20"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={handleTotalBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setEditingTotal(false); } }}
                    autoFocus
                    inputMode="decimal"
                  />
                ) : isReadOnly ? (
                  <span>{formatNumber(convertedTotal, 2)}</span>
                ) : (
                  <span
                    className="cursor-pointer hover:text-company-main transition"
                    onClick={() => {
                      setEditValue(formatNumber(convertedTotal, 2));
                      setEditingTotal(true);
                    }}
                  >
                    {formatNumber(convertedTotal, 2)}
                  </span>
                )}
              </td>
            </tr>

            {/* DKK total sub-line when non-DKK selected */}
            {displayCurrency !== 'DKK' && (
              <tr className="text-sm text-black/40">
                <td className="py-0.5 font-medium">Total:</td>
                <td className="py-0.5">{currencyBadge('DKK', false)}</td>
                <td className="py-0.5 text-right font-medium">
                  {formatNumber(total, 2)}
                </td>
              </tr>
            )}

            {/* Profit */}
            <tr className="text-sm text-black/70">
              <td className="py-1.5 font-medium text-black/80">
                Profit (net):
              </td>
              <td className="py-1.5">{dkkBadge}</td>
              <td className="py-1.5 text-right font-semibold text-black/80">
                {formatNumber(profit, 2)} (
                {formatNumber(profitPercent, 2)}%)
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
);
}

export default TotalsSection;